import React from 'react';
import styled from 'styled-components';
import _ from 'lodash';
import { Flex, Padding } from 'components/base/layout-components';
import Text from 'components/base/Text';
import { TRACKING } from 'common/constants/constants';
import { SETTINGS_VALUES, API_TOKENS_STATUS } from './api-tokens.constants';
import { isAdmin } from 'common/services/accounts.service';
import { Tooltip } from '@material-ui/core';
import Table from 'components/base/Table';
import { getActiveTokens } from './api-tokens.util';
import { revokeToken } from 'common/services/api-tokens.service';
import { createCreateApiTokenModal } from 'modals/modal-options-factory';
import * as TrackingService from 'common/services/tracking.service';
import { SettingValues } from '@linearb/linjs-common';
import RevokeTokenConfirmationDialog from './RevokeTokenConfirmationDialog';
import {
  ApiTokensActions,
  LabelColumn,
  TokenField,
  LastAccessedDateColumn,
  CreatedByColumn
} from './ApiTokensColumns';
import { INCIDENTS_STRATEGIES } from '../Settings/components/IncidentDetectionSetting/incidents-detection-settings.constants';
import useSettingsService from 'components/hooks/useSettingsService';
import { useSelector } from 'react-redux';
import { settingsSelector } from 'state/selectors/settings';
import { LBButton } from '@linearb/buzz';

const MAX_TOKENS_COUNT = 20;
const TableBox = styled.div`
  .MuiTableHead-root > .MuiTableRow-head:first-child {
    border-top: 0;
  }

  .MuiTableBody-root > .MuiTableRow-root:last-child > .MuiTableCell-root {
    border-bottom: 0;
  }
`;

const TableConfig = [
  { name: 'LABEL', dataPoint: 'label' },
  {
    name: 'TOKEN',
    dataPoint: 'token'
  },
  { name: 'CREATED BY', dataPoint: 'created_by' },
  { name: 'LAST ACCESSED', dataPoint: 'last_accessed_at' },
  { name: 'ACTIONS', dataPoint: 'action' }
];

const toast = (toggleSnackBar, message, isSuccess) => {
  toggleSnackBar({
    message: (
      <Text size='large' color='white'>
        {message}
      </Text>
    ),
    variant: isSuccess ? 'success' : 'error'
  });
};

const getTokensTable = ({ tableData }) => (
  <TableBox>
    <Table columns={TableConfig} data={tableData} />
  </TableBox>
);

const CreateApiTokenButton = ({ isAdminAccount, toggleModal, ...props }) => {
  const button = (
    <LBButton
      data-testid='create-api-token-button'
      data-cy='create-api-token-button-data-cy'
      disabled={!isAdminAccount}
      onClick={() => {
        toggleModal(
          createCreateApiTokenModal({
            ...props,
            showGoToSettingsMessage: false,
            onCancel: () => toggleModal()
          })
        );
      }}
    >
      Create API Token
    </LBButton>
  );

  return (
    <Tooltip
      disableHoverListener={isAdminAccount}
      title='You need admin permissions to create an API token'
    >
      <div>{button}</div>
    </Tooltip>
  );
};

const ApiTokens = ({
  toggleSnackBar,
  account,
  apiTokens = [],
  setApiTokens,
  repositories = [],
  toggleModal
}) => {
  const [revokingToken, setRevokingToken] = React.useState(null);
  const { getSettings } = useSettingsService();
  const allSettings = useSelector(settingsSelector);
  const apiReleaseDetectionsSettings = React.useMemo(() => {
    const repositoriesMap = _.keyBy(repositories, 'id');

    return getSettings({ name: SettingValues.RELEASES_STRATEGY })
      .filter(
        item =>
          _.get(item, 'value.key', '') ===
          SETTINGS_VALUES.RELEASE_DETECTION.API_RELEASE_DETECTION
      )
      .map(item => ({
        ...item,
        repository_name: _.get(repositoriesMap[item.repositoryId], 'name')
      }));
  }, [allSettings]);

  const incidentDetectionSettings = React.useMemo(
    () =>
      getSettings({ name: SettingValues.INCIDENTS_STRATEGY }).filter(
        item => _.get(item, 'value.key', '') === INCIDENTS_STRATEGIES.REPORTED
      ),
    [allSettings]
  );

  const isAdminAccount = React.useMemo(
    () => (account ? isAdmin(account) : null),
    [account]
  );
  const activeTokens = React.useMemo(
    () => getActiveTokens(apiTokens),
    [apiTokens]
  );

  const getTableData = () => {
    return apiTokens?.map(apiToken => {
      return {
        label: <LabelColumn apiToken={apiToken} />,
        token: <TokenField apiToken={apiToken} />,
        created_by: <CreatedByColumn apiToken={apiToken} />,
        last_accessed_at: <LastAccessedDateColumn apiToken={apiToken} />,
        action: (
          <ApiTokensActions
            apiToken={apiToken}
            isAdminAccount={isAdminAccount}
            apiReleaseDetectionsSettings={apiReleaseDetectionsSettings}
            incidentDetectionSettings={incidentDetectionSettings}
            onClickRevokeToken={() => setRevokingToken(apiToken)}
          />
        )
      };
    });
  };

  const tableData = React.useMemo(getTableData, [apiTokens]);

  const onConfirmRevokeToken = async () => {
    TrackingService.logEvent(TRACKING.EVENTS.CLICK, {
      page: window.location.pathname,
      click_type: TRACKING.CLICK.REVOKE_API_TOKEN,
      token_id: _.get(revokingToken, 'id'),
      token_prefix: _.get(revokingToken, 'prefix')
    });
    const apiToken = { ...revokingToken };
    setRevokingToken(null);

    try {
      setApiTokens([
        ...apiTokens.filter(({ id }) => id !== apiToken.id),
        {
          ...apiToken,
          status: API_TOKENS_STATUS.REVOKED,
          original_token: null
        }
      ]);

      await revokeToken(apiToken);
      toast(toggleSnackBar, 'Token was revoked successfully', true);
    } catch (exp) {
      setApiTokens([
        ...apiTokens.filter(({ id }) => id !== apiToken.id),
        {
          ...apiToken,
          status: API_TOKENS_STATUS.ACTIVE
        }
      ]);

      toast(toggleSnackBar, 'Oops! There was an issue revoking token', false);
    }
  };

  return (
    <Padding l='medium' r='medium' t='medium'>
      <Padding>
        <Flex items='center' justify='space-between'>
          <div>
            <Text
              size='large'
              pt='1.3rem'
              pb='1.3rem'
              padding='large'
              weight={400}
            >
              API Tokens
            </Text>
            <Text size='medium' color='shuttleGray' pb='1rem' weight={400}>
              Your API tokens should be treated as securely as passwords.
            </Text>
          </div>
          {(!activeTokens || activeTokens.length < MAX_TOKENS_COUNT) && (
            <CreateApiTokenButton
              isAdminAccount={isAdminAccount}
              toggleModal={toggleModal}
              apiTokens={apiTokens}
              setApiTokens={setApiTokens}
              toggleSnackBar={toggleSnackBar}
              account={account}
            />
          )}
        </Flex>

        {getTokensTable({ tableData })}
      </Padding>
      {revokingToken && (
        <RevokeTokenConfirmationDialog
          revokingToken={revokingToken}
          onCancel={() => {
            setRevokingToken(null);
          }}
          onConfirm={() => {
            onConfirmRevokeToken();
          }}
        />
      )}
    </Padding>
  );
};

export default ApiTokens;
